// NativeTTS.ts
'use client';
import type { ITTSEngine, TTSOptions, TTSCapabilities } from './types';

export class NativeTTS implements ITTSEngine {
  private synth: SpeechSynthesis | null = null;
  private currentUtterance: SpeechSynthesisUtterance | null = null;
  private currentCallbacks: Partial<TTSOptions> = {};
  private voicesLoaded = false;
  private voiceCache: SpeechSynthesisVoice[] = [];

  constructor() {}

  GetEngineName(): string {
    return 'Native';
  }

  isAvailable(): boolean {
    return typeof window !== 'undefined' && 'speechSynthesis' in window;
  }

  async initialize(): Promise<void> {
    if (!this.isAvailable()) {
      throw new Error('Web Speech API not supported');
    }

    this.synth = window.speechSynthesis;

    // Voices may load asynchronously
    if (this.synth.getVoices().length === 0) {
      await new Promise<void>((resolve) => {
        const handler = () => {
          this.voiceCache = this.synth?.getVoices() || [];
          this.voicesLoaded = true;
          this.synth?.removeEventListener('voiceschanged', handler);
          resolve();
        };
        this.synth?.addEventListener('voiceschanged', handler);
      });
    } else {
      this.voiceCache = this.synth.getVoices();
      this.voicesLoaded = true;
    }
  }

  async speak(options: TTSOptions): Promise<void> {
    if (!this.synth) throw new Error('NativeTTS not initialized');

    // Cancel any ongoing speech
    this.stop();

    this.currentCallbacks = options;

    const utterance = new SpeechSynthesisUtterance(options.text);
    this.currentUtterance = utterance;

    // Configure voice
    utterance.lang = options.voice?.startsWith('en') ? options.voice : 'en-US';
    utterance.rate = options.speed ?? 1.0;
    utterance.pitch = options.pitch ?? 1.0;

    // Select best available voice
    const voice = this.selectVoice(options.voice);
    if (voice) {
      utterance.voice = voice;
    }

    // Event handlers
    utterance.onstart = () => {
      options.onStart?.();
    };

    utterance.onend = () => {
      this.currentUtterance = null;
      options.onEnd?.();
    };

    utterance.onerror = (event) => {
      this.currentUtterance = null;
      const error = new Error(`Speech synthesis error: ${event.error}`);
      options.onError?.(error);
    };

    // iOS requires speak() to be called directly in user gesture handler
    // We can't await it, so we wrap in promise
    return new Promise((resolve, reject) => {
      const originalOnEnd = utterance.onend;
      const originalOnError = utterance.onerror;

      utterance.onend = (event) => {
        originalOnEnd?.call(utterance, event);
        resolve();
      };

      utterance.onerror = (event) => {
        originalOnError?.call(utterance, event);
        reject(new Error(`Speech error: ${event.error}`));
      };

      this.synth?.speak(utterance);
    });
  }

  stop(): void {
    this.synth?.cancel();
    this.currentUtterance = null;
  }

  pause(): void {
    this.synth?.pause();
  }

  resume(): void {
    this.synth?.resume();
  }

  getCapabilities(): TTSCapabilities {
    return {
      supportsStreaming: false, // Web Speech API doesn't stream
      supportsOffline: true, // Works offline once voices loaded
      maxTextLength: 50000, // Browser-dependent, usually very high
    };
  }

  destroy(): void {
    this.stop();
    this.synth = null;
  }

  // Get available voices (useful for UI voice picker)
  getVoices(): SpeechSynthesisVoice[] {
    return this.voiceCache.length > 0
      ? this.voiceCache
      : this.synth?.getVoices() || [];
  }

  // Get English voices specifically
  getEnglishVoices(): SpeechSynthesisVoice[] {
    return this.getVoices().filter((v) => v.lang.startsWith('en'));
  }

  private selectVoice(preferredName?: string): SpeechSynthesisVoice | null {
    const voices = this.getVoices();

    // 1. Try preferred voice if specified
    if (preferredName) {
      const exact = voices.find((v) => v.name === preferredName);
      if (exact) return exact;
    }

    // 2. Try high-quality Google voice
    const googleVoice = voices.find(
      (v) => v.lang.startsWith('en') && v.name.includes('Google'),
    );
    if (googleVoice) return googleVoice;

    // 3. Try Microsoft voice (good quality on Windows)
    const msVoice = voices.find(
      (v) => v.lang.startsWith('en') && v.name.includes('Microsoft'),
    );
    if (msVoice) return msVoice;

    // 4. Try Apple voice (good on macOS/iOS)
    const appleVoice = voices.find(
      (v) => v.lang.startsWith('en') && v.name.includes('Samantha'),
    );
    if (appleVoice) return appleVoice;

    // 5. Fallback to any English voice
    return voices.find((v) => v.lang.startsWith('en')) || voices[0] || null;
  }
}
